import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { diffWordsWithSpace, structuredPatch } from 'diff';
import { useMemo } from 'react';
import type { PromptSectionSnapshotEntry } from 'services/types/businessUnitPromptVersion';
import {
  NAVY,
  RADIUS_ITEM,
  STATUS_ERROR,
  STATUS_GOOD,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_TINT,
} from 'styles/tokens';

type DiffKind = 'context' | 'added' | 'removed';

export interface DiffLine {
  kind: DiffKind;
  text: string;
  oldLine: number | null;
  newLine: number | null;
}

export interface DiffHunk {
  header: string;
  lines: DiffLine[];
}

interface DiffToken {
  text: string;
  emphasized: boolean;
}

export const buildPromptDiffHunks = (oldText: string, newText: string): DiffHunk[] => {
  const patch = structuredPatch('base', 'compare', oldText, newText, '', '', { context: 3 });
  return patch.hunks.map((hunk) => {
    let oldLine = hunk.oldStart;
    let newLine = hunk.newStart;
    const lines: DiffLine[] = [];
    for (const rawLine of hunk.lines) {
      if (!rawLine.startsWith('\\')) {
        const prefix = rawLine[0];
        if (prefix === '-') {
          lines.push({ kind: 'removed', text: rawLine.slice(1), oldLine, newLine: null });
          oldLine += 1;
        } else if (prefix === '+') {
          lines.push({ kind: 'added', text: rawLine.slice(1), oldLine: null, newLine });
          newLine += 1;
        } else {
          lines.push({ kind: 'context', text: rawLine.slice(1), oldLine, newLine });
          oldLine += 1;
          newLine += 1;
        }
      }
    }
    return { header: `@@ -${hunk.oldStart},${hunk.oldLines} +${hunk.newStart},${hunk.newLines} @@`, lines };
  });
};

const lineBackground = (kind: DiffKind): string => {
  if (kind === 'added') return alpha(STATUS_GOOD, 0.12);
  if (kind === 'removed') return alpha(STATUS_ERROR, 0.1);
  return 'transparent';
};

const linePrefix = (kind: DiffKind): string => (kind === 'added' ? '+' : kind === 'removed' ? '-' : ' ');

const LineNumber = ({ value }: { value: number | null }) => (
  <Box
    component="span"
    sx={{
      minWidth: 42,
      px: 1,
      py: 0.35,
      textAlign: 'right',
      color: alpha(NAVY, 0.45),
      bgcolor: SURFACE_TINT,
      userSelect: 'none',
    }}
  >
    {value ?? ''}
  </Box>
);

const buildLineTokenMap = (hunks: DiffHunk[]): Map<DiffLine, DiffToken[]> => {
  const tokens = new Map<DiffLine, DiffToken[]>();
  for (const hunk of hunks) {
    let index = 0;
    while (index < hunk.lines.length) {
      const line = hunk.lines[index];
      if (line.kind === 'context') {
        tokens.set(line, [{ text: line.text, emphasized: false }]);
        index += 1;
      } else {
        const removed: DiffLine[] = [];
        const added: DiffLine[] = [];
        while (index < hunk.lines.length && hunk.lines[index].kind === 'removed') removed.push(hunk.lines[index++]);
        while (index < hunk.lines.length && hunk.lines[index].kind === 'added') added.push(hunk.lines[index++]);

        const pairedLines = Math.max(removed.length, added.length);
        for (let pairIndex = 0; pairIndex < pairedLines; pairIndex += 1) {
          const oldLine = removed[pairIndex];
          const newLine = added[pairIndex];
          if (oldLine && newLine) {
            const wordChanges = diffWordsWithSpace(oldLine.text, newLine.text);
            tokens.set(
              oldLine,
              wordChanges
                .filter((change) => !change.added)
                .map((change) => ({ text: change.value, emphasized: Boolean(change.removed) }))
            );
            tokens.set(
              newLine,
              wordChanges
                .filter((change) => !change.removed)
                .map((change) => ({ text: change.value, emphasized: Boolean(change.added) }))
            );
          } else if (oldLine) {
            tokens.set(oldLine, [{ text: oldLine.text, emphasized: true }]);
          } else if (newLine) {
            tokens.set(newLine, [{ text: newLine.text, emphasized: true }]);
          }
        }
      }
    }
  }
  return tokens;
};

const DiffText = ({ line, tokens }: { line: DiffLine; tokens: DiffToken[] | undefined }) => (
  <Box component="span" sx={{ py: 0.35, pr: 2, whiteSpace: 'pre-wrap', minWidth: 300 }}>
    {(tokens ?? [{ text: line.text, emphasized: false }]).map((token, index) => (
      <Box
        // Token order is stable within its line and duplicate text fragments are possible.
        key={`${index}-${token.text}`}
        component="span"
        sx={{
          bgcolor: token.emphasized ? alpha(line.kind === 'added' ? STATUS_GOOD : STATUS_ERROR, 0.22) : 'transparent',
        }}
      >
        {token.text || ' '}
      </Box>
    ))}
  </Box>
);

const UnifiedDiff = ({ hunks, tokenMap }: { hunks: DiffHunk[]; tokenMap: Map<DiffLine, DiffToken[]> }) => (
  <Box sx={{ overflowX: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }}>
    {hunks.map((hunk) => (
      <Box key={hunk.header} sx={{ mb: 1.5 }}>
        <Box sx={{ px: 2, py: 0.75, bgcolor: alpha(NAVY, 0.06), color: NAVY }}>{hunk.header}</Box>
        {hunk.lines.map((line, index) => (
          <Box
            key={`${hunk.header}-${index}`}
            sx={{ display: 'flex', minWidth: 'max-content', bgcolor: lineBackground(line.kind) }}
          >
            <LineNumber value={line.oldLine} />
            <LineNumber value={line.newLine} />
            <Box component="span" sx={{ width: 24, py: 0.35, textAlign: 'center', userSelect: 'none' }}>
              {linePrefix(line.kind)}
            </Box>
            <DiffText line={line} tokens={tokenMap.get(line)} />
          </Box>
        ))}
      </Box>
    ))}
  </Box>
);

interface SplitRow {
  left: DiffLine | null;
  right: DiffLine | null;
}

export const toSplitRows = (lines: DiffLine[]): SplitRow[] => {
  const rows: SplitRow[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (line.kind === 'context') {
      rows.push({ left: line, right: line });
      index += 1;
    } else {
      const removed: DiffLine[] = [];
      const added: DiffLine[] = [];
      while (index < lines.length && lines[index].kind === 'removed') removed.push(lines[index++]);
      while (index < lines.length && lines[index].kind === 'added') added.push(lines[index++]);
      const length = Math.max(removed.length, added.length);
      for (let rowIndex = 0; rowIndex < length; rowIndex += 1) {
        rows.push({ left: removed[rowIndex] ?? null, right: added[rowIndex] ?? null });
      }
    }
  }
  return rows;
};

const SplitCell = ({ line, side, tokens }: { line: DiffLine | null; side: 'old' | 'new'; tokens?: DiffToken[] }) => (
  <Box sx={{ display: 'flex', minWidth: 0, bgcolor: line ? lineBackground(line.kind) : SURFACE_TINT }}>
    <LineNumber value={line ? (side === 'old' ? line.oldLine : line.newLine) : null} />
    <Box component="span" sx={{ width: 22, py: 0.35, textAlign: 'center', flexShrink: 0 }}>
      {line ? linePrefix(line.kind) : ''}
    </Box>
    {line ? (
      <DiffText line={line} tokens={tokens} />
    ) : (
      <Box component="span" sx={{ py: 0.35, pr: 1 }}>
        {' '}
      </Box>
    )}
  </Box>
);

const SplitDiff = ({ hunks, tokenMap }: { hunks: DiffHunk[]; tokenMap: Map<DiffLine, DiffToken[]> }) => (
  <Box sx={{ overflowX: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }}>
    {hunks.map((hunk) => (
      <Box key={hunk.header} sx={{ mb: 1.5, minWidth: 760 }}>
        <Box sx={{ px: 2, py: 0.75, bgcolor: alpha(NAVY, 0.06), color: NAVY }}>{hunk.header}</Box>
        {toSplitRows(hunk.lines).map((row, index) => (
          <Box key={`${hunk.header}-${index}`} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            <SplitCell line={row.left} side="old" tokens={row.left ? tokenMap.get(row.left) : undefined} />
            <SplitCell line={row.right} side="new" tokens={row.right ? tokenMap.get(row.right) : undefined} />
          </Box>
        ))}
      </Box>
    ))}
  </Box>
);

interface SectionSnapshotChange {
  name: string;
  oldSection: PromptSectionSnapshotEntry | null;
  newSection: PromptSectionSnapshotEntry | null;
}

const sectionSnapshotsEqual = (
  oldSection: PromptSectionSnapshotEntry | null,
  newSection: PromptSectionSnapshotEntry | null
): boolean =>
  oldSection?.displayName === newSection?.displayName &&
  oldSection?.value === newSection?.value &&
  oldSection?.permission === newSection?.permission &&
  oldSection?.externalId === newSection?.externalId &&
  oldSection?.externalModifiedAt === newSection?.externalModifiedAt;

const buildSectionSnapshotChanges = (
  oldSnapshot: PromptSectionSnapshotEntry[] | null,
  newSnapshot: PromptSectionSnapshotEntry[] | null
): SectionSnapshotChange[] => {
  // A null snapshot is used by the prompt/tools editor to mean “sections unchanged from the
  // comparison base”; persisted versions always return an explicit array.
  const resolvedOldSnapshot = oldSnapshot ?? newSnapshot ?? [];
  const resolvedNewSnapshot = newSnapshot ?? oldSnapshot ?? [];
  const oldByName = new Map(resolvedOldSnapshot.map((section) => [section.name, section]));
  const newByName = new Map(resolvedNewSnapshot.map((section) => [section.name, section]));
  return [...new Set([...oldByName.keys(), ...newByName.keys()])]
    .sort((left, right) => left.localeCompare(right))
    .map((name) => ({ name, oldSection: oldByName.get(name) ?? null, newSection: newByName.get(name) ?? null }))
    .filter(({ oldSection, newSection }) => !sectionSnapshotsEqual(oldSection, newSection));
};

const SnapshotValueDiff = ({
  oldValue,
  newValue,
  layout,
}: {
  oldValue: string;
  newValue: string;
  layout: 'unified' | 'split';
}) => {
  const hunks = useMemo(() => buildPromptDiffHunks(oldValue, newValue), [oldValue, newValue]);
  const tokenMap = useMemo(() => buildLineTokenMap(hunks), [hunks]);
  if (!hunks.length) return null;
  return layout === 'unified' ? (
    <UnifiedDiff hunks={hunks} tokenMap={tokenMap} />
  ) : (
    <SplitDiff hunks={hunks} tokenMap={tokenMap} />
  );
};

const SectionSnapshotDiff = ({
  oldSnapshot,
  newSnapshot,
  layout,
}: {
  oldSnapshot: PromptSectionSnapshotEntry[] | null;
  newSnapshot: PromptSectionSnapshotEntry[] | null;
  layout: 'unified' | 'split';
}) => {
  const changes = useMemo(() => buildSectionSnapshotChanges(oldSnapshot, newSnapshot), [oldSnapshot, newSnapshot]);

  return (
    <Box sx={{ bgcolor: SURFACE_CONTAINER_LOW, borderRadius: RADIUS_ITEM, p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 600, mb: 1 }}>
        Cambios en secciones del prompt
      </Typography>
      {changes.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {changes.map(({ name, oldSection, newSection }) => {
            const status = !oldSection ? 'Añadida' : !newSection ? 'Eliminada' : 'Actualizada';
            const statusColor = !oldSection ? STATUS_GOOD : !newSection ? STATUS_ERROR : NAVY;
            const statusBackground = !oldSection
              ? alpha(STATUS_GOOD, 0.12)
              : !newSection
                ? alpha(STATUS_ERROR, 0.1)
                : alpha(NAVY, 0.08);
            return (
              <Box key={name} sx={{ bgcolor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_ITEM, overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography
                    sx={{ color: NAVY, fontWeight: 600, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                  >
                    &lt;SECTION:{name}&gt;
                  </Typography>
                  <Chip
                    label={status}
                    size="small"
                    sx={{ color: statusColor, bgcolor: statusBackground, fontWeight: 600 }}
                  />
                </Box>
                {oldSection?.displayName !== newSection?.displayName && (
                  <Typography variant="body2" sx={{ px: 1.5, pb: 1, color: alpha(NAVY, 0.65) }}>
                    {oldSection?.displayName ?? 'Sin nombre'} → {newSection?.displayName ?? 'Sin nombre'}
                  </Typography>
                )}
                {oldSection?.permission !== newSection?.permission && (
                  <Typography variant="body2" sx={{ px: 1.5, pb: 1, color: alpha(NAVY, 0.65) }}>
                    Permiso: {oldSection?.permission ?? 'Sin sección'} → {newSection?.permission ?? 'Sin sección'}
                  </Typography>
                )}
                <SnapshotValueDiff
                  oldValue={oldSection?.value ?? ''}
                  newValue={newSection?.value ?? ''}
                  layout={layout}
                />
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: alpha(NAVY, 0.55) }}>
          Sin cambios en las secciones referenciadas
        </Typography>
      )}
    </Box>
  );
};

interface PromptVersionDiffProps {
  oldPrompt: string | null;
  newPrompt: string | null;
  oldFunctions: string[] | null;
  newFunctions: string[] | null;
  oldSectionSnapshot: PromptSectionSnapshotEntry[] | null;
  newSectionSnapshot: PromptSectionSnapshotEntry[] | null;
  layout: 'unified' | 'split';
}

const PromptVersionDiff = ({
  oldPrompt,
  newPrompt,
  oldFunctions,
  newFunctions,
  oldSectionSnapshot,
  newSectionSnapshot,
  layout,
}: PromptVersionDiffProps) => {
  const hunks = useMemo(() => buildPromptDiffHunks(oldPrompt ?? '', newPrompt ?? ''), [oldPrompt, newPrompt]);
  const tokenMap = useMemo(() => buildLineTokenMap(hunks), [hunks]);
  const oldSet = useMemo(() => new Set(oldFunctions ?? []), [oldFunctions]);
  const newSet = useMemo(() => new Set(newFunctions ?? []), [newFunctions]);
  const addedFunctions = [...newSet].filter((name) => !oldSet.has(name)).sort();
  const removedFunctions = [...oldSet].filter((name) => !newSet.has(name)).sort();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, maxWidth: '100%' }}>
      <Box sx={{ bgcolor: SURFACE_CONTAINER_LOW, borderRadius: RADIUS_ITEM, p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 600, mb: 1 }}>
          Cambios en tools
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {removedFunctions.map((name) => (
            <Chip
              key={`removed-${name}`}
              label={`− ${name}`}
              size="small"
              sx={{ color: STATUS_ERROR, bgcolor: alpha(STATUS_ERROR, 0.1) }}
            />
          ))}
          {addedFunctions.map((name) => (
            <Chip
              key={`added-${name}`}
              label={`+ ${name}`}
              size="small"
              sx={{ color: STATUS_GOOD, bgcolor: alpha(STATUS_GOOD, 0.12) }}
            />
          ))}
          {!addedFunctions.length && !removedFunctions.length && (
            <Typography variant="body2" sx={{ color: alpha(NAVY, 0.55) }}>
              Sin cambios en tools
            </Typography>
          )}
        </Box>
      </Box>
      <SectionSnapshotDiff oldSnapshot={oldSectionSnapshot} newSnapshot={newSectionSnapshot} layout={layout} />
      <Box sx={{ bgcolor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_ITEM, overflow: 'hidden' }}>
        {hunks.length ? (
          layout === 'unified' ? (
            <UnifiedDiff hunks={hunks} tokenMap={tokenMap} />
          ) : (
            <SplitDiff hunks={hunks} tokenMap={tokenMap} />
          )
        ) : (
          <Typography variant="body2" sx={{ color: alpha(NAVY, 0.55), p: 2 }}>
            Sin cambios en el prompt
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PromptVersionDiff;
